import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Check, CreditCard, Shield, Clock, BookOpen, Tag, X, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { courseData } from '../data/courseData';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Header from '../components/Header';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState('');
  
  const { user, isLoading: authLoading } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    
    setDiscountLoading(true);
    setDiscountError('');
    
    try {
      const response = await axios.post(`${API}/payments/validate-discount`, {
        code: discountCode,
        package_id: 'sovereignty_course'
      });
      
      if (response.data.valid) {
        setAppliedDiscount(response.data);
        setDiscountError('');
      } else {
        setDiscountError(response.data.error || 'Invalid discount code');
        setAppliedDiscount(null);
      }
    } catch (err) {
      console.error('Discount validation error:', err);
      setDiscountError('Failed to validate code');
      setAppliedDiscount(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
  };

  const handleCheckout = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: 'sovereignty_course',
        user_email: user.email,
        origin_url: window.location.origin,
        discount_code: appliedDiscount ? discountCode : null
      });

      // Check if free access was granted
      if (response.data.free_access_granted) {
        navigate('/course');
        return;
      }

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to start checkout. Please try again.');
      setIsLoading(false);
    }
  };

  const features = [
    "7 days of structured sovereignty content",
    "Daily action items and exercises",
    "Personal journaling with guided prompts",
    "Progress tracking with calendar view",
    "Lifetime access to course materials"
  ];

  const originalPrice = 10;
  const finalPrice = appliedDiscount ? appliedDiscount.discounted_price : originalPrice;
  const isFree = finalPrice === 0;

  if (authLoading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-stone-950' : 'bg-stone-50'}`}>
        <Header />
        <div className="pt-28 pb-20 px-4 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-stone-950' : 'bg-stone-50'}`}>
      <Header />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Course Info */}
            <div className={`rounded-3xl border p-8 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
              {/* Course badge */}
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 ${isDark ? 'bg-amber-500/10' : 'bg-amber-100'}`}>
                <BookOpen className={`w-4 h-4 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                <span className={`text-sm font-medium ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>Premium Course</span>
              </div>

              <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                {courseData.title}
              </h1>
              <p className={`text-lg mb-6 ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                {courseData.subtitle}
              </p>

              <p className={`mb-8 leading-relaxed ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                {courseData.description}
              </p>

              {/* Features */}
              <div className="space-y-3 mb-8">
                <h3 className={`font-semibold ${isDark ? 'text-stone-200' : 'text-stone-700'}`}>What's Included:</h3>
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-green-500' : 'text-green-600'}`} />
                    <span className={isDark ? 'text-stone-300' : 'text-stone-600'}>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Course days preview */}
              <div className={`p-4 rounded-xl ${isDark ? 'bg-stone-800/50' : 'bg-stone-50'}`}>
                <h4 className={`font-medium mb-3 ${isDark ? 'text-stone-300' : 'text-stone-700'}`}>7 Days of Transformation:</h4>
                <div className="grid grid-cols-2 gap-2">
                  {courseData.days.slice(0, 4).map((day) => (
                    <div key={day.day} className={`flex items-center gap-2 text-sm ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${isDark ? 'bg-stone-700' : 'bg-stone-200'}`}>
                        {day.day}
                      </span>
                      <span className="truncate">{day.title}</span>
                    </div>
                  ))}
                  <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>
                    <span>+3 more days...</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Card */}
            <div className={`rounded-3xl border p-8 h-fit sticky top-28 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
              {/* Price */}
              <div className="text-center mb-6">
                {appliedDiscount ? (
                  <div className="space-y-1">
                    <div className="flex items-center justify-center gap-3">
                      <span className={`text-2xl line-through ${isDark ? 'text-stone-600' : 'text-stone-400'}`}>
                        ${originalPrice}
                      </span>
                      <span className={`text-5xl font-bold ${isFree ? 'text-green-500' : isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                        {isFree ? 'FREE' : `$${finalPrice.toFixed(2)}`}
                      </span>
                      {!isFree && <span className={`text-lg ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>USD</span>}
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Tag className="w-4 h-4 text-green-500" />
                      <span className="text-green-500 font-medium">
                        {appliedDiscount.description} applied!
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className={`text-5xl font-bold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>$10</span>
                      <span className={`text-lg ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>USD</span>
                    </div>
                    <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>One-time payment • Lifetime access</p>
                  </>
                )}
              </div>

              {/* Discount Code Input */}
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                  Have a discount code?
                </label>
                {appliedDiscount ? (
                  <div className={`flex items-center justify-between p-3 rounded-xl border ${isDark ? 'bg-green-500/10 border-green-500/30' : 'bg-green-50 border-green-200'}`}>
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-green-500" />
                      <span className="font-mono font-medium text-green-500">{discountCode.toUpperCase()}</span>
                    </div>
                    <button
                      onClick={handleRemoveDiscount}
                      className={`p-1 rounded-full transition-colors ${isDark ? 'hover:bg-stone-800' : 'hover:bg-stone-200'}`}
                    >
                      <X className={`w-4 h-4 ${isDark ? 'text-stone-400' : 'text-stone-500'}`} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className={`font-mono uppercase ${
                        isDark 
                          ? 'bg-stone-800 border-stone-700 text-stone-100' 
                          : 'bg-stone-50 border-stone-200'
                      }`}
                      onKeyDown={(e) => e.key === 'Enter' && handleApplyDiscount()}
                      data-testid="discount-code-input"
                    />
                    <Button
                      onClick={handleApplyDiscount}
                      disabled={discountLoading || !discountCode.trim()}
                      variant="outline"
                      className={isDark ? 'border-stone-700 hover:bg-stone-800' : 'border-stone-300 hover:bg-stone-100'}
                      data-testid="apply-discount-btn"
                    >
                      {discountLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Apply'
                      )}
                    </Button>
                  </div>
                )}
                {discountError && (
                  <p className="text-red-500 text-sm mt-2">{discountError}</p>
                )}
              </div>

              {/* Trust badges */}
              <div className="flex justify-center gap-6 mb-6">
                <div className="flex flex-col items-center gap-1">
                  <Shield className={`w-6 h-6 ${isDark ? 'text-green-500' : 'text-green-600'}`} />
                  <span className={`text-xs ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>Secure</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <CreditCard className={`w-6 h-6 ${isDark ? 'text-blue-500' : 'text-blue-600'}`} />
                  <span className={`text-xs ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>Stripe</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <Clock className={`w-6 h-6 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                  <span className={`text-xs ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>Instant</span>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-500 text-sm text-center">
                  {error}
                </div>
              )}

              {/* Checkout button */}
              <Button
                onClick={handleCheckout}
                disabled={isLoading}
                className={`w-full h-14 font-semibold text-lg ${
                  isFree 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : 'bg-amber-500 hover:bg-amber-600 text-stone-950'
                }`}
                data-testid="checkout-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className={`w-5 h-5 border-2 rounded-full animate-spin ${isFree ? 'border-white/30 border-t-white' : 'border-stone-950/30 border-t-stone-950'}`} />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {isFree ? (
                      <>
                        <Check className="w-5 h-5" />
                        Get Free Access
                      </>
                    ) : (
                      <>
                        <Lock className="w-5 h-5" />
                        Get Instant Access
                      </>
                    )}
                  </span>
                )}
              </Button>

              <p className={`text-center text-xs mt-4 ${isDark ? 'text-stone-600' : 'text-stone-400'}`}>
                {isFree ? 'No payment required' : "You'll be redirected to Stripe for secure payment"}
              </p>

              {/* Logged in as */}
              <div className={`mt-6 pt-6 border-t text-center ${isDark ? 'border-stone-800' : 'border-stone-200'}`}>
                <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
                  Purchasing as: <span className="font-medium">{user.email}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
